import { Injectable } from '@angular/core';
import {
  MessagePosition,
  MessageType,
  SnackBarService,
} from '@epicuro-next/ataraxia/snack-bar';
import { ProductListApiService } from '@epicuro-next/data/engagement/engagement-api';
import { EngagementProductListWithUser } from '@epicuro-next/data/engagement/engagement-models';
import { IProductListPatchModel } from '@epicuro-next/data/lists';
import { List, ProductReference } from '@epicuro-next/models/engagement';
import { APP_PRIVATE_PATHS } from '@epicuro-next/navigation/constants';
import { LoggerService } from '@epicuro-next/platform/monitoring';
import {
  loadList,
  resetListProducts,
  setUpdatingList,
} from '@epicuro-next/state/list-entities';
import { generateListName } from '@epicuro-next/state/product-list-state';
import { ACTIVE_LIST_FAILED_MESSAGE } from '@epicuro-next/state/product-list-state/constants';
import { go, selectRouteParams } from '@epicuro-next/state/router-store';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import { showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { unique } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Product } from 'graphql-api/gql-api-interfaces';
import {
  BehaviorSubject,
  concatMap,
  EMPTY,
  forkJoin,
  Observable,
  of,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  scan,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import * as actions from '../../actions';
import { PatchProducts } from '../../actions';
import { selectActiveListId } from '../../selectors';
import { reversePatchItemsToAdd } from '../../utils';

const VIEW_LIST_MESSAGE = $localize`:productBasketSnackViewList|View list message on snackbar after add products to a list@@gotoListSnackBarMessage:View List`;

enum PatchModelAction {
  ADD,
  DELETE,
}

/**
 * Modify Items Effects
 */
@Injectable()
export class ModifyItemsEffects {
  private patchedProducts$$ = new BehaviorSubject(undefined);

  /**
   * Effect to modify an item in a ProductList
   */
  public addProductsToList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addProductsToList),
      filter((action) => !action.onlyStateUpdate),
      concatLatestFrom(({ list: { id } }) =>
        this.store.pipe(
          select(selectRouteParams),
          map((params) => params?.id === id),
        ),
      ),
      map(([action, isSourceFromOpenedList]) => {
        const patchModel = createPatchModel(
          PatchModelAction.ADD,
          action.products,
          isSourceFromOpenedList,
        );

        return actions.patchProductsBulk({
          listId: action.list.id,
          patchModel,
        });
      }),
    ),
  );

  /**
   * Effect to signal optimistic success patching a product list
   */
  public addProductsToListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addProductsToList),
      switchMap(({ list, products, onlyStateUpdate }) => {
        return onlyStateUpdate
          ? of({
              list,
              products,
            })
          : this.actions$.pipe(
              ofType(
                actions.patchProductsBulkSuccess,
                actions.patchProductsBulkFailed,
              ),
              take(1),
              map(() => ({
                list,
                products,
              })),
            );
      }),
      switchMap(({ list, products }) => {
        let message = $localize`:productBasketAdddedToActiveList|Added product message@@addedProductMessageToActiveList:Added ${products[0].title} to ${list.name}`;

        if (products.length > 1) {
          message = $localize`:productBasketMultipleAdddedToActiveList|Added multiple products message@@addedMultipleProductMessageToActiveList:Added multiple products to ${list.name}`;
        }
        this.snackbarService.show(
          message,
          MessagePosition.BOTTOM,
          MessageType.DEFAULT,
          VIEW_LIST_MESSAGE,
        );

        return this.snackbarService.triggeredAction.pipe(
          map(() =>
            go({
              commands: [
                APP_PRIVATE_PATHS.browse.path,
                APP_PRIVATE_PATHS.productList.path,
                list.id,
              ],
            }),
          ),
        );
      }),
    ),
  );

  /**
   * Effect to modify an item in a ProductList
   */
  public deleteProductsFromList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductsFromList),
      map((action) => {
        const patchModel = createPatchModel(
          PatchModelAction.DELETE,
          action.products,
        );

        const listId = action.list?.id ?? '';
        return actions.patchProductsBulk({
          listId,
          patchModel,
        });
      }),
    ),
  );

  /**
   * Effect to signal optimistic success patching a product list
   */
  public deleteProductsToListSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductsFromList),
      concatLatestFrom(() => this.store.pipe(select(selectActiveListId))),
      map(([{ products, list }, activeListId]) => {
        let message: string;
        if (activeListId === list.id) {
          message = $localize`:productBasketDeletedFromActiveList|Deleted product message@@deletedProductMessageFromActiveList:Deleted ${products[0].title} from active list`;

          if (products.length > 1) {
            message = $localize`:productBasketMultipleDeletedFromActiveList|Deleted multiple products message@@deletedMultipleProductMessageFromActiveList:Deleted multiple products from active list`;
          }
        } else {
          message = $localize`:productBasketDeletedFromOpenedList|Deleted product message from opened list@@deletedProductMessageFromOpenedList:Deleted ${products[0].title} from ${list.name} list`;

          if (products.length > 1) {
            message = $localize`:productBasketMultipleDeletedFromOpenedList|Deleted multiple products message from opened list@@deletedMultipleProductMessageFromOpenedList:Deleted multiple products from ${list.name} list`;
          }
        }

        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.SUCCESS,
        });
      }),
    ),
  );

  /**
   * Collect all consequent requests over N time and execute them at once
   * as API supports that
   */
  public patchProductsBulk$ = createEffect(() =>
    this.patchedProducts$$
      .pipe(
        switchMap(() =>
          this.actions$.pipe(
            ofType(actions.patchProductsBulk),
            map(({ type, ...v }) => v),
            scan(
              (acc: PatchProducts[], val: PatchProducts): PatchProducts[] => {
                const patchedBulkExtend = acc.find(
                  ({ listId }) => listId === val.listId,
                );
                if (!!patchedBulkExtend) {
                  return [
                    ...acc.filter(({ listId }) => listId !== val.listId),
                    {
                      ...patchedBulkExtend,
                      patchModel: getExtendPatch(
                        patchedBulkExtend.patchModel,
                        val.patchModel,
                      ),
                    },
                  ];
                }

                return [...acc, val];
              },
              [],
            ),
          ),
        ),
      )
      .pipe(
        debounceTime(1000),
        map((patchedProductLists) =>
          patchedProductLists
            .filter(
              ({ patchModel: { itemsToDelete, itemsToAdd } }) =>
                !!itemsToDelete.length || !!itemsToAdd.length,
            )
            .map(reversePatchItemsToAdd),
        ),
        filter((patchProducts) => !!patchProducts.length),
        // to nullable scan operator, so we listen & batching new actions
        tap(() => this.patchedProducts$$.next(undefined)),
        mergeMap((patchProducts: PatchProducts[]) =>
          forkJoin(
            patchProducts.map((patch) =>
              this.productListApi.changeProducts({
                id: patch.listId,
                patchItems: patch.patchModel,
              }),
            ),
          ).pipe(
            map(() =>
              actions.patchProductsBulkSuccess({ products: patchProducts }),
            ),
            catchError(() =>
              of(actions.patchProductsBulkFailed({ products: patchProducts })),
            ),
          ),
        ),
      ),
  );

  public setListUpdatingOnPatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchProductsBulk),
      map((action) => {
        return setUpdatingList({
          listId: action.listId,
          isUpdating: true,
        });
      }),
    ),
  );

  public setListUpdatingOnPatchFinish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchProductsBulkSuccess, actions.patchProductsBulkFailed),
      switchMap(({ products }) => {
        return products
          .map(({ listId }) => listId)
          .filter(unique)
          .map((listId) =>
            setUpdatingList({
              listId,
              isUpdating: false,
            }),
          );
      }),
    ),
  );
  /**
   * Effect to signal failure patching a product list
   */
  public modifyItemInListFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.patchProductsBulkFailed),
      tap(() => {
        this.loggerService.exception({
          error: new Error(
            'Network error during removing of products in a product list',
          ),
        });
      }),
      map(({ products }) => {
        const message = $localize`:productBasketModifyNetworkError|Network error product message@@productBasketModifyNetworkError:Network error has happened. Reverting your last ${products
          .map((p) => p.patchModel.itemsToAdd.length)
          .reduce((acc, val) => acc + val)} added, ${products
          .map((p) => p.patchModel.itemsToDelete.length)
          .reduce((acc, val) => acc + val)} removed items.`;

        return showSnackBarMessage({
          message,
          position: MessagePosition.BOTTOM,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );

  public addNextAvailableVariant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.addNextAvailableVariantToList),
      concatMap(({ list, product }) => {
        const list$ = list
          ? of(list as EngagementProductListWithUser)
          : this.createActiveList$;

        return list$.pipe(
          switchMap((listToUpdate: EngagementProductListWithUser) => {
            this.store.dispatch(
              setUpdatingList({
                listId: listToUpdate.id,
                isUpdating: true,
              }),
            );
            return this.productListApi
              .changeProducts({
                id: listToUpdate.id,
                patchItems: {
                  itemsToAdd: [
                    {
                      productId: product?.parentId as string,
                      brandId: product?.brandId as string,
                    },
                  ],
                  itemsToDelete: [],
                },
                stylesOnly: true,
              })
              .pipe(
                switchMap((resp) => {
                  const addedProduct = this.getUpdatedVariantFromProduct(
                    product,
                    resp.addedItems[0],
                  );
                  return [
                    actions.addProductsToList({
                      list: listToUpdate as List,
                      products: [addedProduct as Product],
                      onlyStateUpdate: true,
                    }),
                    loadList({ listId: listToUpdate.id }),
                    resetListProducts({ listId: listToUpdate.id }),
                    actions.addNextAvailableVariantToListSuccess({
                      list: listToUpdate as List,
                      product,
                    }),
                    setUpdatingList({
                      listId: listToUpdate.id,
                      isUpdating: false,
                    }),
                  ];
                }),
                catchError(() => {
                  return [
                    showSnackBarMessage({
                      message: $localize`:productBasketAdddedToActiveList|Added product failure message@@addedProductMessageFailureToActiveList:Failed to add ${product.title} to ${listToUpdate.name}`,
                      messageType: MessageType.DANGER,
                    }),
                    actions.addNextAvailableVariantToListFailure({
                      list: list as List,
                      product,
                    }),
                    setUpdatingList({
                      listId: listToUpdate.id,
                      isUpdating: false,
                    }),
                  ];
                }),
              );
          }),
        );
      }),
    );
  });

  private readonly createActiveList$: Observable<EngagementProductListWithUser> =
    this.store.select(selectAccountId).pipe(
      withLatestFrom(this.store.select(selectSiteId)),
      concatMap(([accountId, siteId]) =>
        this.productListApi
          .create({
            name: generateListName(),
            accountId: accountId as string,
            siteId,
          })
          .pipe(
            concatMap((resp) => {
              return this.productListApi
                .setActive(resp.id)
                .pipe(map(() => resp));
            }),
            catchError(() => {
              this.store.dispatch(
                showSnackBarMessage({
                  message: ACTIVE_LIST_FAILED_MESSAGE,
                  messageType: MessageType.DANGER,
                }),
              );
              return EMPTY;
            }),
          ),
      ),
    );

  public getUpdatedVariantFromProduct(
    product: Product | null,
    variant: ProductReference,
  ) {
    const allProducts = [product, ...(product?.relatedVariants ?? [])];
    let updatedVariant = allProducts.find(
      (x) => x?.productId === variant.productId,
    );

    // fallback if response sends a variant that is not in the product variants
    if (!updatedVariant && product) {
      updatedVariant = {
        ...product,
        title: product.articleTitle,
        brandId: variant.brandId ?? product.brandId,
        productId: variant.productId,
      };
    }

    return {
      ...updatedVariant,
      brandId: updatedVariant?.brandId ?? product?.brandId,
    };
  }

  constructor(
    private store: Store,
    private actions$: Actions,
    private productListApi: ProductListApiService,
    private loggerService: LoggerService,
    private snackbarService: SnackBarService,
  ) {}
}

function createPatchModel(
  action: PatchModelAction,
  products: Product[],
  isSourceFromOpenedList: boolean = true,
) {
  const mappedProducts = products.map((product) => ({
    productId: product.productId,
    brandId: product.brandId,
    isAvailable: product?.isAvailable ?? true,
    isSourceFromOpenedList,
  }));

  return action === PatchModelAction.ADD
    ? { itemsToAdd: mappedProducts, itemsToDelete: [] }
    : { itemsToAdd: [], itemsToDelete: mappedProducts };
}

/**
 * v1 - {add: [1,2,3], del: [5,6]},
 * v2 - {add: [6], del: [1,7]}
 * res - {add: [2,3], del: [5,7]}
 *
 * algorithm: Combine all (v1, v2 by same field) and then filter all by oposite
 * e.g. res: { add: [1,2,3,6].fil([5,6]).fil(1,7) => [2,3]; del: [5,6,1,7].fil(1,2,3).fil(6) => [5,7] }
 */
export function getExtendPatch(
  patchModel: IProductListPatchModel,
  extendPatchModel: IProductListPatchModel,
): IProductListPatchModel {
  const itemsToAdd = patchModel.itemsToAdd
    .concat(extendPatchModel.itemsToAdd)
    .filter(
      (v) =>
        !patchModel.itemsToDelete.some(
          (toDelete) => toDelete.productId === v.productId,
        ),
    )
    .filter(
      (v) =>
        !extendPatchModel.itemsToDelete.some(
          (toDelete) => toDelete.productId === v.productId,
        ),
    );

  const itemsToDelete = patchModel.itemsToDelete
    .concat(extendPatchModel.itemsToDelete)
    .filter(
      (v) =>
        !patchModel.itemsToAdd.some((toAdd) => toAdd.productId === v.productId),
    )
    .filter(
      (v) =>
        !extendPatchModel.itemsToAdd.some(
          (toAdd) => toAdd.productId === v.productId,
        ),
    );

  return {
    itemsToAdd,
    itemsToDelete,
  };
}
